import {useEffect, useRef, useState} from "react";
import {Button, TextField, LinearProgress} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import JoditEditor from "jodit-react";
import axios from "axios";
import {useNavigate, useParams} from "react-router-dom";

const EditNewsForm = () => {
    const {id} = useParams();
    const [formData, setFormData] = useState(null);
    const [isFetchingNews, setIsFetchingNews] = useState(false);
    const [isSavingNews, setIsSavingNews] = useState(false);
    const [loadError, setLoadError] = useState("");
    const editor = useRef(null);
    const navigate = useNavigate();

    const saveNewsEdit = async (editedNews) => {
        setIsSavingNews(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/EditNews`, editedNews);
            navigate("/dashboard/newsmanager");
        } catch (error) {
            setLoadError("Failed to save news item");
        } finally {
            setIsSavingNews(false);
        }
    };

    useEffect(() => {
        if (!id) return;
        let isActive = true;
        setIsFetchingNews(true);
        setLoadError("");
        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/GetNewsById`, {params: {Id: id}})
            .then((res) => {
                const payload = res?.data?.data || {};
                if (!isActive) return;
                setFormData({
                    id: payload.id ?? null,
                    isTemplate: payload.isTemplate ?? false,
                    title: payload.title ?? "",
                    titleEng: payload.titleEng ?? "",
                    descriptionShort: payload.descriptionShort ?? "",
                    description: payload.description ?? "",
                    descriptionShortEng: payload.descriptionShortEng ?? "",
                    descriptionEng: payload.descriptionEng ?? "",
                    buttonText: payload.buttonText ?? "",
                    buttonTextEng: payload.buttonTextEng ?? "",
                    path: payload.path ?? "",
                    file: undefined
                });
            })
            .catch(() => setLoadError("Failed to load news item"))
            .finally(() => setIsFetchingNews(false));
        return () => {
            isActive = false;
        };
    }, [id]);

    const georgianTranslations = {
        "Type something": " ",
        "Bold": "ტექსტის გამუქება",
        "Italic": "დახრილი ტექსტის შექმნა",
        "Underline": "ტექსტის ხაზგასმა",
        "Insert Image": "სურათის ჩასმა",
        "Clear Formatting": "ფორმატირების წაშლა",
        "Insert Format Block": "ფორმატირების ბლოკის ჩასმა",
        "Strike through": "გადახაზული",
        "Insert Unordered List": "ბურთულებიანი სიის შექმნა",
        "Insert Ordered List": "დანომრილი სიის შექმნა",
        "Font family": "შრიფტის არჩევა",
        "Font size": "შრიფტის ზომა",
        "Line height": "ხაზის დაშორება",
        "Insert Horizontal Line": "ხაზის დაშორება",
        "Increase Indent": "დაშორების გაზრდა",
        "Decrease Indent": "დაშორების შემცირება",
        "Undo": "უკუსვლა",
        "Redo": "საწყისი",
        "Align": "სწორება",
        "Insert Special Character": "სპეც. სიმბოლო",
        "Fill color or set the text color": "ფერის შეცვლა",
        "Background": "ფონი",
        "Text": "ტექსტი",
        "Insert format block": "ფორმატირება",
        "Print": "დაბეჭვდა",
        "Show all": "მეტის ნახვა",
        "Insert link": "ბმლის ჩასმა",
        "URL": "ბმული",
        "Open in new tab": "ახალ გვერდზე გახსნა",
        "Insert": "ჩასმა"
    };

    const config = {
        readonly: false,
        toolbarButtonSize: "middle",
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        language: "ka",
        i18n: {ka: georgianTranslations},
        height: 800,
        width: 1100,
        disablePlugins: [
            "table",
            "spellcheck",
            "speech-recognize",
            "open-in-fullsize",
            "add-new-line",
            "Insert-file",
            "ai-assistant",
            "file",
            "image",
            "video",
            "fullsize",
            "about",
            "preview",
            "symbols"
        ],
        controls: {
            font: {
                list: {Arial: "Arial", Sylfaen: "Sylfaen"}
            }
        },
        buttons: [
            "bold","italic","underline","strikethrough","|",
            "ul","ol","|",
            "outdent","indent","|",
            "font","fontsize","brush","paragraph","|",
            "image","table","link","|",
            "align","undo","redo","|",
            "hr","eraser","copyformat","|",
            "symbol","fullsize","print","about"
        ]
    };

    const handleChange = (e) => {
        if (typeof e === "string") {
            setFormData((prev) => ({...prev, description: e}));
        } else {
            const {name, value, type, checked} = e.target;
            setFormData((prev) => ({...prev, [name]: type === "checkbox" ? checked : value}));
        }
    };

    const handleChangeDescriptionShort = (e) => {
        if (typeof e === "string") {
            setFormData((prev) => ({...prev, descriptionShort: e}));
        } else {
            const {name, value, type, checked} = e.target;
            setFormData((prev) => ({...prev, descriptionShort: type === "checkbox" ? checked : value}));
        }
    };

    const handleChangeDescriptionShortEnd = (e) => {
        if (typeof e === "string") {
            setFormData((prev) => ({...prev, descriptionShortEng: e}));
        } else {
            const {name, value, type, checked} = e.target;
            setFormData((prev) => ({...prev, descriptionShortEng: type === "checkbox" ? checked : value}));
        }
    };

    const handleChangeDescriptionEnd = (e) => {
        if (typeof e === "string") {
            setFormData((prev) => ({...prev, descriptionEng: e}));
        } else {
            const {name, value, type, checked} = e.target;
            setFormData((prev) => ({...prev, descriptionEng: type === "checkbox" ? checked : value}));
        }
    };

    const handleCheckBox = (isChecked) => {
        setFormData((prev) => ({...prev, isTemplate: isChecked}));
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;
        if (selectedFile.type !== "image/png") return;
        const reader = new FileReader();
        reader.onload = (readEvent) => {
            const base64Payload = String(readEvent.target?.result || "");
            const encoded = base64Payload.includes(",") ? base64Payload.split(",")[1] : base64Payload;
            setFormData((prev) => ({
                ...prev,
                file: {base64String: encoded, ext: "png"}
            }));
        };
        reader.readAsDataURL(selectedFile);
    };

    const modalContainerStyle = {
        background: "#fff",
        borderRadius: 15,
        width: "100%"
    };

    const headerStyle = {
        padding: "16px 20px",
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: 18,
        fontWeight: 600
    };

    const contentStyle = {
        padding: 20,
        overflowY: "auto",
        display: "grid",
        rowGap: 16
    };

    const footerStyle = {
        padding: 16,
        borderTop: "1px solid #e5e7eb",
        display: "flex",
        justifyContent: "flex-end",
        gap: 12
    };

    const isFormDisabled = isFetchingNews || isSavingNews;

    return (
        <div style={{width: "100%"}}>
            {isFetchingNews && <LinearProgress />}
            <div style={modalContainerStyle}>
                <div style={headerStyle}>
                    <span>სიახლეების რედაქტირება</span>
                </div>

                <div style={contentStyle}>
                    {loadError && <div style={{color: "#b91c1c"}}>{loadError}</div>}

                    <TextField
                        margin="dense"
                        label="სათაური"
                        type="text"
                        fullWidth
                        variant="outlined"
                        name="title"
                        value={formData?.title || ""}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                    />

                    <TextField
                        margin="dense"
                        label="ღილაკის ტექსტი"
                        type="text"
                        fullWidth
                        variant="outlined"
                        name="buttonText"
                        value={formData?.buttonText || ""}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                    />

                    <TextField
                        margin="dense"
                        label="ღილაკის ტექსტი ინგლისურად"
                        type="text"
                        fullWidth
                        variant="outlined"
                        name="buttonTextEng"
                        value={formData?.buttonTextEng || ""}
                        onChange={handleChange}
                        disabled={isFormDisabled}
                    />

                    <div style={{fontSize: 16, fontWeight: 600}}>სრული ტექსტი ქართულად</div>
                    <JoditEditor
                        ref={editor}
                        value={formData?.description || ""}
                        config={{...config, readonly: isFormDisabled, }}
                        tabIndex={1}
                        onBlur={(content) => handleChange(content)}
                    />

                    <div style={{fontSize: 16, fontWeight: 600}}>მოკლე აღწერა ქართულად</div>
                    <JoditEditor
                        ref={editor}
                        value={formData?.descriptionShort || ""}
                        config={{...config, readonly: isFormDisabled, limitChars: 200}}
                        tabIndex={1}
                        onBlur={(content) => handleChangeDescriptionShort(content)}
                    />

                    <div style={{fontSize: 16, fontWeight: 600}}>სრული ტექსტი ინგლისურად</div>
                    <JoditEditor
                        ref={editor}
                        value={formData?.descriptionEng || ""}
                        config={{...config, readonly: isFormDisabled}}
                        tabIndex={1}
                        onBlur={(content) => handleChangeDescriptionEnd(content)}
                    />

                    <div style={{fontSize: 16, fontWeight: 600}}>მოკლე აღწერა ინგლისურად</div>
                    <JoditEditor
                        ref={editor}
                        value={formData?.descriptionShortEng || ""}
                        config={{...config, readonly: isFormDisabled, limitChars: 200}}
                        tabIndex={1}
                        onBlur={(content) => handleChangeDescriptionShortEnd(content)}
                    />

                    <TextField
                        type="file"
                        onChange={handleFileChange}
                        inputProps={{accept: "image/png"}}
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        disabled={isFormDisabled}

                    />

                    <div style={{display: "flex", alignItems: "center", gap: 8}}>
                        <input
                            type="checkbox"
                            checked={!!formData?.isTemplate}
                            onChange={(e) => handleCheckBox(e.target.checked)}
                            style={{width: 18, height: 18, cursor: "pointer", accentColor: "#3872F2"}}
                            disabled={isFormDisabled}
                        />
                        <label style={{fontSize: 16, cursor: "pointer"}}>შაბლონად შენახვა</label>
                    </div>
                </div>

                <div style={footerStyle}>
                    <LoadingButton
                        onClick={() => saveNewsEdit(formData)}
                        loading={isSavingNews}
                        variant="contained"
                    >
                        შენახვა
                    </LoadingButton>
                </div>
            </div>
        </div>
    );
};

export default EditNewsForm;
